import { useEffect, useMemo, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Dialog, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import { BankAccountField } from './bank-account-field';
import {
  InvoiceDialogContent,
  InvoiceDialogFormShell,
  InvoiceDialogRightPane,
} from './invoice-dialog-shell';
import { InvoiceCreatePdfProvider, useCreatePdfFileState } from './invoice-create-pdf-context';
import {
  buildProvisionalDialogDescription,
  buildProvisionalDialogTitle,
} from './provisional-dialog-copy';
import { ModelFormFields } from './model-form-fields';
import { ResolvedDocumentSection } from './resolved-document-section';
import { advanceOpenOf, useAllocatedDownpayment } from '../api/use-allocated-downpayment';
import { usePricingReadiness } from '../api/use-pricing-readiness';
import { pricingReadinessBlockers } from '../lib/pricing-readiness-labels';
import { ProvisionalGates } from './provisional-dialog-gates';
import type { InvoiceRow } from '../types';
import {
  PROVISIONAL_SUBMIT_FIELDS,
  type ProvisionalSubmitValues,
} from './provisional-invoice-schema';
import {
  applyTradePrefill,
  buildProvisionalDefaults,
  type BlContextForTradeInvoice,
  type TicketContextForTradeInvoice,
  type TradePrefill,
} from './trade-invoice-payload';
import { TradeInvoiceDialogActions } from './trade-invoice-actions';
import { EditReclassifyField } from './trade-invoice-reclassify-field';
import { reclassificationReasonMissing } from './trade-invoice-reclassify';
import { useSaveProvisionalMutation } from './use-save-trade-mutation';
import { applyCoreValuesPrefill, useCoreValuesProvisionalPrefill } from './use-core-values-prefill';
import { useSuggestedNumberPrefill } from './use-suggested-number-prefill';

export interface ProvisionalInvoiceDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly side: 'buy' | 'sell';
  readonly orderId: string;
  readonly blOrderId: string;
  readonly existingInvoice: InvoiceRow | null;
  readonly ticket: TicketContextForTradeInvoice;
  readonly bl: BlContextForTradeInvoice;
  readonly prefill?: TradePrefill;
  readonly onSaved: () => void;
}

interface InnerProps extends Omit<ProvisionalInvoiceDialogProps, 'prefill'> {
  readonly allocatedDownpayment: number;
  readonly advanceOpen: number | null;
  readonly pricingReady: boolean;
  readonly missingFields: string[];
  readonly ticketId: string | null;
  readonly readinessLoaded: boolean;
  readonly prefill: TradePrefill | undefined;
  readonly rightPane: ReactNode;
}

function useProvisionalForm(props: InnerProps) {
  const { side, ticket, bl, allocatedDownpayment, advanceOpen, existingInvoice, prefill } = props;
  // The value log's current expectation beats the ticket/BL guess as the base
  // (only for fresh invoices); an AI extraction prefill still wins on top.
  const logPrefill = useCoreValuesProvisionalPrefill(props.blOrderId, side);
  const defaults = useMemo(() => {
    const base = buildProvisionalDefaults({
      side,
      ticket,
      bl,
      allocatedDownpayment,
      advanceOpen,
      existing: existingInvoice,
    });
    const withLog = existingInvoice === null ? applyCoreValuesPrefill(base, logPrefill) : base;
    return applyTradePrefill(withLog, prefill);
  }, [side, ticket, bl, allocatedDownpayment, advanceOpen, existingInvoice, prefill, logPrefill]);
  const form = useForm<ProvisionalSubmitValues>({
    resolver: zodResolver(PROVISIONAL_SUBMIT_FIELDS),
    defaultValues: defaults,
  });

  useEffect(() => {
    if (props.open) form.reset(defaults);
  }, [props.open, defaults, form]);

  useSuggestedNumberPrefill(form, 'invoice_number', {
    invoiceType: 'Provisional',
    direction: side === 'buy' ? 'Purchase' : 'Sale',
    blOrderId: props.blOrderId,
    enabled: props.open && existingInvoice === null,
  });

  return form;
}

function useProvisionalSubmit(props: InnerProps, form: ReturnType<typeof useProvisionalForm>) {
  const mutation = useSaveProvisionalMutation({
    side: props.side,
    orderId: props.orderId,
    blOrderId: props.blOrderId,
    existingInvoice: props.existingInvoice,
    onSaved: props.onSaved,
    onOpenChange: props.onOpenChange,
    // The backend reserves ai_extraction_id for payables, so only the buy side
    // forwards the extraction linkage; sell-side AI Extract prefills without it.
    ...(props.side === 'buy' && props.prefill !== undefined
      ? { aiExtractionId: props.prefill.aiExtractionId }
      : {}),
  });
  const onSubmit = form.handleSubmit((values) => {
    if (props.existingInvoice !== null && reclassificationReasonMissing(values, 'Provisional')) {
      form.setError('reclassification_reason', {
        type: 'required',
        message: 'A reason is required when reclassifying invoice type.',
      });
      return;
    }
    mutation.mutate(values);
  });
  return { mutation, onSubmit };
}

function ProvisionalFormBody({
  props,
  form,
  isPending,
}: {
  readonly props: InnerProps;
  readonly form: ReturnType<typeof useProvisionalForm>;
  readonly isPending: boolean;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>{buildProvisionalDialogTitle(props.side, props.existingInvoice)}</DialogTitle>
        <DialogDescription>
          {buildProvisionalDialogDescription(props.existingInvoice)}
        </DialogDescription>
      </DialogHeader>
      <ProvisionalGates props={props} />
      <ModelFormFields
        kind="Provisional"
        side={props.side}
        register={form.register}
        control={form.control}
        errors={form.formState.errors}
        disabled={isPending}
        invoiceNumberPlaceholder="PROVISIONAL-001"
      />
      {props.side === 'sell' && (
        <BankAccountField
          registration={form.register('payment_account_key')}
          disabled={isPending}
        />
      )}
      <ResolvedDocumentSection invoice={props.existingInvoice} kind="Provisional" />
      <EditReclassifyField
        form={form}
        existingInvoice={props.existingInvoice}
        disabled={isPending}
        currentType="Provisional"
      />
    </>
  );
}

function DialogFormInner(props: InnerProps) {
  const form = useProvisionalForm(props);
  const { mutation, onSubmit } = useProvisionalSubmit(props, form);
  const blockers = pricingReadinessBlockers(props);
  const saveDisabled = mutation.isPending || blockers.length > 0;
  return (
    <form
      onSubmit={(e) => {
        void onSubmit(e);
      }}
      noValidate
      className="flex min-h-0 flex-1 flex-col"
    >
      <InvoiceDialogFormShell
        rightPane={props.rightPane}
        footer={
          <TradeInvoiceDialogActions
            isPending={mutation.isPending}
            saveDisabled={saveDisabled}
            blockers={blockers}
            submitLabel={
              props.existingInvoice !== null ? 'Save Changes' : 'Create Provisional Invoice'
            }
            onCancel={() => {
              props.onOpenChange(false);
            }}
          />
        }
      >
        <ProvisionalFormBody props={props} form={form} isPending={mutation.isPending} />
      </InvoiceDialogFormShell>
    </form>
  );
}

interface DialogBodyProps extends Omit<ProvisionalInvoiceDialogProps, 'prefill'> {
  readonly prefill: TradePrefill | undefined;
  readonly rightPane: ReactNode;
}

function DialogBody(props: DialogBodyProps) {
  const direction = props.side === 'buy' ? 'Purchase' : 'Sale';
  const allocatedQuery = useAllocatedDownpayment(props.blOrderId, direction);
  const readinessQuery = usePricingReadiness(props.blOrderId, direction);
  const allocated =
    allocatedQuery.data === undefined ? 0 : Number(allocatedQuery.data.allocated_downpayment);
  const advanceOpen = advanceOpenOf(allocatedQuery.data);
  // Default `pricingReady` to `false` until the readiness check resolves so
  // a fast operator can't submit during the loading window before the gate
  // is known. While loading we also suppress the blocked-alert to avoid
  // flashing an empty list of missing fields.
  const pricingReady =
    readinessQuery.data === undefined ? false : readinessQuery.data.has_required_pricing_inputs;
  const missingFields = readinessQuery.data?.missing_fields ?? [];
  const ticketId = readinessQuery.data?.ticket_id ?? null;
  const readinessLoaded = readinessQuery.data !== undefined;
  return (
    <DialogFormInner
      {...props}
      allocatedDownpayment={allocated}
      advanceOpen={advanceOpen}
      pricingReady={pricingReady}
      missingFields={missingFields}
      ticketId={ticketId}
      readinessLoaded={readinessLoaded}
    />
  );
}

export function ProvisionalInvoiceDialog(props: ProvisionalInvoiceDialogProps) {
  const [pdfFile, setPdfFile] = useCreatePdfFileState(props.open, props.prefill?.file ?? null);
  const existing = props.existingInvoice;
  const rightPane = (
    <InvoiceDialogRightPane
      existingInvoice={existing}
      file={pdfFile}
      onFileChange={setPdfFile}
      side={props.side}
    />
  );
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <InvoiceDialogContent>
        <InvoiceCreatePdfProvider value={{ file: pdfFile }}>
          {props.open && <DialogBody {...props} prefill={props.prefill} rightPane={rightPane} />}
        </InvoiceCreatePdfProvider>
      </InvoiceDialogContent>
    </Dialog>
  );
}
